import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useReveal } from "./useReveal";
import { fireConfetti } from "./confetti";
import "./EcoAcademy.css";

/**
 * ECO ACADEMY — the education hub. Real, sourced statistics only.
 * Animation techniques: scroll-triggered count-up counters, animated gradients,
 * expressive staggered typography, infinite marquee, glass cards, micro-interactions.
 */

type Stat = { value: number; suffix: string; label: string; source: string; color: string };

const STATS: Stat[] = [
  { value: 95, suffix: "%", label: "of the energy is saved when a can is made from recycled aluminum instead of new metal", source: "U.S. EPA", color: "var(--green)" },
  { value: 22, suffix: "%", label: "of the world's e-waste is formally collected & recycled — the rest is lost or dumped", source: "UN Global E-Waste Monitor 2024", color: "var(--blue)" },
  { value: 17, suffix: " trees", label: "are saved for every single ton of paper that gets recycled", source: "U.S. EPA", color: "var(--green-deep)" },
  { value: 85, suffix: "%", label: "of textiles in the U.S. end up landfilled or burned — most could be reused", source: "U.S. EPA", color: "var(--orange)" },
];

const TREASURE = [
  { icon: "🥉", amount: "35,000+ lbs", metal: "of copper" },
  { icon: "🥈", amount: "772 lbs", metal: "of silver" },
  { icon: "🥇", amount: "75 lbs", metal: "of gold" },
];

type VaultFact = { cat: "ewaste" | "clothing" | "people" | "nyc"; text: string; source: string };

const VAULT_CATS = [
  { id: "all", label: "🌎 All" },
  { id: "ewaste", label: "💻 E-Waste" },
  { id: "clothing", label: "👕 Clothing" },
  { id: "people", label: "🤝 Helping People" },
  { id: "nyc", label: "🗽 NYC Rules" },
] as const;

const VAULT: VaultFact[] = [
  { cat: "ewaste", text: "E-waste is the fastest-growing type of trash on Earth — the world produced about 62 million tonnes in a single year, and it keeps climbing.", source: "UN Global E-Waste Monitor 2024" },
  { cat: "ewaste", text: "Only about 1 in 5 pounds of the world's e-waste gets properly recycled. The rest is burned, buried, or forgotten in drawers.", source: "UN Global E-Waste Monitor 2024" },
  { cat: "ewaste", text: "A tonne of old circuit boards can contain more gold than a tonne of ore from an actual gold mine.", source: "United Nations" },
  { cat: "ewaste", text: "Electronics are a small slice of what we throw away — but they account for up to 70% of the toxic heavy metals in landfills.", source: "U.S. EPA" },
  { cat: "ewaste", text: "Loose lithium batteries in the trash spark hundreds of garbage-truck and facility fires every year. Always recycle them separately.", source: "U.S. EPA" },
  { cat: "ewaste", text: "Most of a laptop's carbon footprint comes from manufacturing it — so keeping one alive longer (or refurbishing it) is one of the greenest moves there is.", source: "Apple & Dell environmental reports" },
  { cat: "clothing", text: "One cotton t-shirt takes about 2,700 liters of water to make — roughly what one person drinks in two and a half years.", source: "WWF" },
  { cat: "clothing", text: "The average American throws away around 81 pounds of clothing every single year.", source: "Council for Textile Recycling" },
  { cat: "clothing", text: "Wearing your clothes just 9 months longer cuts their carbon, water, and waste footprint by 20–30%.", source: "WRAP UK" },
  { cat: "clothing", text: "Nearly 100% of textiles are reusable or recyclable — yet the vast majority end up in the trash anyway.", source: "Council for Textile Recycling" },
  { cat: "people", text: "Roughly 1 in 4 NYC households lacks home broadband. A refurbished laptop can be someone's bridge to school, work, and telehealth.", source: "NYC Comptroller" },
  { cat: "people", text: "When you give us a working laptop, we wipe it clean, fix it up, and get it to a student or family who needs it — free.", source: "The Everything Eco way" },
  { cat: "people", text: "Your closet clean-out becomes free clothes for neighbors — winter coats, school clothes, and job-interview outfits.", source: "The Everything Eco way" },
  { cat: "people", text: "A donated couch or bed helps a family moving out of a shelter turn an empty apartment into a home.", source: "The Everything Eco way" },
  { cat: "nyc", text: "In New York State it's actually against the law to put most electronics in the regular trash — they must be recycled.", source: "NYS Electronic Equipment Recycling & Reuse Act" },
  { cat: "nyc", text: "Plastic bags do NOT go in NYC curbside recycling — they jam the sorting machines. Bring them to store drop-off bins instead.", source: "NYC Dept. of Sanitation" },
  { cat: "nyc", text: "Greasy pizza boxes contaminate paper recycling. Tear off and recycle the clean parts; trash the greasy ones.", source: "NYC recycling guidance" },
  { cat: "nyc", text: "NYC residents can't be fined for donating — but tossing e-waste curbside can cost you. Booking a free pickup is literally the easier AND legal move.", source: "NYS e-waste law + common sense" },
];

type Quiz = { q: string; options: string[]; answer: number; why: string };
const QUIZ: Quiz[] = [
  {
    q: "Where should old batteries go?",
    options: ["Regular trash", "A battery drop-off / e-waste pickup", "The recycling bin with bottles"],
    answer: 1,
    why: "Batteries can spark fires in trash trucks and leak toxins in landfills — they need special handling. We take them on pickups! 🔋",
  },
  {
    q: "A greasy pizza box — recyclable?",
    options: ["Yes, it's cardboard!", "No — grease ruins paper recycling"],
    answer: 1,
    why: "Oil soaks into the fibers and contaminates the whole batch. Clean parts can be torn off and recycled. 🍕",
  },
  {
    q: "What's inside one recycled laptop?",
    options: ["Just plastic", "Copper, silver, gold & rare metals", "Nothing reusable"],
    answer: 1,
    why: "Circuit boards are little treasure chests — that's why e-waste is the fastest-growing (and most valuable) waste stream on Earth. 💻",
  },
  {
    q: "Plastic shopping bags go…",
    options: ["In curbside recycling", "To store drop-offs / reuse", "In the ocean, apparently"],
    answer: 1,
    why: "Bags jam sorting machines at recycling plants! Reuse them or use store drop-off bins. And yes — never the ocean. 🌊",
  },
];

/** Scroll-triggered count-up number. */
const CountUp: React.FC<{ value: number; suffix: string; color: string }> = ({ value, suffix, color }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        const t0 = performance.now();
        const dur = 1600;
        const step = (t: number) => {
          const p = Math.min(1, (t - t0) / dur);
          const eased = 1 - Math.pow(1 - p, 3);
          setN(Math.round(value * eased));
          if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [value]);
  return (
    <div ref={ref} className="ea-stat-num" style={{ color }}>
      {n}
      <span className="ea-stat-suffix">{suffix}</span>
    </div>
  );
};

const EcoAcademy: React.FC = () => {
  useReveal();
  const [qi, setQi] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [vaultCat, setVaultCat] = useState<string>("all");
  const vaultFacts = vaultCat === "all" ? VAULT : VAULT.filter((f) => f.cat === vaultCat);

  const pick = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (i === QUIZ[qi].answer) setScore((s) => s + 1);
  };
  const next = () => {
    if (qi + 1 >= QUIZ.length) {
      setDone(true);
      if (score + 0 >= 3) fireConfetti();
    } else {
      setQi((q) => q + 1);
      setPicked(null);
    }
  };
  const restart = () => { setQi(0); setPicked(null); setScore(0); setDone(false); };

  const HERO_WORDS = "Knowledge is renewable.".split(" ");

  return (
    <div className="ea-page">
      {/* ── Hero ── */}
      <section className="ea-hero">
        <div className="ea-hero-orb a" /><div className="ea-hero-orb b" />
        <div className="ee-container ea-hero-inner">
          <div className="ea-kicker">🎓 ECO ACADEMY</div>
          <h1 className="ea-h1">
            {HERO_WORDS.map((w, i) => (
              <span key={i} style={{ animationDelay: `${0.12 + i * 0.14}s` }}>{w}&nbsp;</span>
            ))}
          </h1>
          <p className="ea-hero-sub">
            The more you know, the more you save — energy, water, money, and the planet.
            Real facts from real sources, made fun. Perfect for kids, classrooms & curious New Yorkers.
          </p>
        </div>
      </section>

      {/* ── Marquee ── */}
      <div className="ea-marquee" aria-hidden="true">
        <div className="ea-marquee-track">
          {[0, 1].map((k) => (
            <span key={k} className="ea-marquee-seg">
              RECYCLE ♻ REUSE ♻ REPAIR ♻ REBIRTH ♻ ELECTRONICS ♻ CLOTHES ♻ FURNITURE ♻ SECOND CHANCES ♻&nbsp;
            </span>
          ))}
        </div>
      </div>

      {/* ── Counters ── */}
      <section className="ee-section ea-stats">
        <div className="ee-container">
          <div className="ee-section-label" style={{ textAlign: "center" }}>The numbers don't lie</div>
          <h2 className="ee-section-title" style={{ textAlign: "center" }}>Facts that hit different 📊</h2>
          <div className="ea-stat-grid">
            {STATS.map((s) => (
              <div key={s.label} className="ea-stat-card">
                <CountUp value={s.value} suffix={s.suffix} color={s.color} />
                <p>{s.label}</p>
                <span className="ea-source">Source: {s.source}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Treasure ── */}
      <section className="ee-section ea-treasure">
        <div className="ee-container" style={{ textAlign: "center" }}>
          <div className="ea-treasure-kicker">HIDDEN TREASURE</div>
          <h2 className="ea-treasure-title">Inside every 1 million recycled cell phones:</h2>
          <div className="ea-treasure-row">
            {TREASURE.map((t, i) => (
              <div key={t.metal} className="ea-treasure-card" style={{ animationDelay: `${i * 0.4}s` }}>
                <div className="ea-treasure-ico">{t.icon}</div>
                <strong>{t.amount}</strong>
                <span>{t.metal}</span>
              </div>
            ))}
          </div>
          <p className="ea-treasure-note">
            Source: U.S. EPA. Your junk drawer is literally a gold mine — that's why we say
            <strong> Trash to Tech</strong>. 💰
          </p>
          <Link to="/pickup" className="ee-btn ee-btn-green">📦 Turn my junk into treasure</Link>
        </div>
      </section>

      {/* ── The Fact Vault ── */}
      <section className="ee-section ea-vault">
        <div className="ee-container">
          <div className="ee-section-label" style={{ textAlign: "center" }}>Stuff most people don't know</div>
          <h2 className="ee-section-title" style={{ textAlign: "center" }}>The Fact Vault 🔐</h2>
          <p className="ea-vault-sub">
            Real facts, real sources — the kind that make you look at your junk drawer differently.
          </p>
          <div className="ea-vault-filters">
            {VAULT_CATS.map((c) => (
              <button
                key={c.id}
                className={`ea-vault-chip${vaultCat === c.id ? " on" : ""}`}
                onClick={() => setVaultCat(c.id)}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="ea-vault-grid">
            {vaultFacts.map((f, i) => (
              <div className="ea-vault-card" key={f.text.slice(0, 30)} style={{ animationDelay: `${(i % 6) * 60}ms` }}>
                <div className="ea-vault-ico">
                  {f.cat === "ewaste" ? "💻" : f.cat === "clothing" ? "👕" : f.cat === "people" ? "🤝" : "🗽"}
                </div>
                <p>{f.text}</p>
                <span className="ea-source">Source: {f.source}</span>
              </div>
            ))}
          </div>
          <p className="ea-vault-cta">
            Got something on this list sitting at home? <Link to="/pickup">That's a free pickup waiting to happen →</Link>
          </p>
        </div>
      </section>

      {/* ── Quiz ── */}
      <section className="ee-section ea-quiz">
        <div className="ee-container ea-quiz-wrap">
          <div className="ee-section-label" style={{ textAlign: "center" }}>Test yourself</div>
          <h2 className="ee-section-title" style={{ textAlign: "center" }}>The 60-Second Eco Quiz ⚡</h2>

          {!done ? (
            <div className="ea-quiz-card" key={qi}>
              <div className="ea-quiz-progress">
                {QUIZ.map((_, i) => (
                  <span key={i} className={`ea-quiz-dot${i === qi ? " on" : i < qi ? " past" : ""}`} />
                ))}
              </div>
              <h3>{QUIZ[qi].q}</h3>
              <div className="ea-quiz-opts">
                {QUIZ[qi].options.map((o, i) => {
                  let cls = "ea-quiz-opt";
                  if (picked !== null) {
                    if (i === QUIZ[qi].answer) cls += " right";
                    else if (i === picked) cls += " wrong";
                    else cls += " dim";
                  }
                  return (
                    <button key={o} className={cls} onClick={() => pick(i)}>
                      {o}
                    </button>
                  );
                })}
              </div>
              {picked !== null && (
                <div className="ea-quiz-why">
                  <strong>{picked === QUIZ[qi].answer ? "✅ Nailed it!" : "❌ Not quite —"}</strong> {QUIZ[qi].why}
                  <button className="ee-btn ee-btn-green ea-quiz-next" onClick={next}>
                    {qi + 1 >= QUIZ.length ? "See my score →" : "Next question →"}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="ea-quiz-card ea-quiz-done">
              <div className="ea-quiz-score">{score}/{QUIZ.length}</div>
              <img
                className="ea-quiz-badge"
                src={`${process.env.PUBLIC_URL}/badge-${score >= 4 ? 4 : score === 3 ? 3 : score === 2 ? 1 : 0}.png`}
                alt="Your Eco Academy badge"
              />
              <h3>
                {score === QUIZ.length ? "PERFECT! You earned the ECO LEGEND badge. 🦸" :
                 score >= 3 ? "PLANET GUARDIAN status — the planet thanks you! 💚" :
                 "You earned a badge — now run it back for a better one! 🌎"}
              </h3>
              <div className="ea-quiz-end-btns">
                <button className="ee-btn ee-btn-blue" onClick={restart}>↻ Try again</button>
                <Link to="/game" className="ee-btn ee-btn-green">🎮 Play the real game</Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="ee-section ea-cta">
        <div className="ee-container" style={{ textAlign: "center" }}>
          <h2 className="ee-section-title">Learned something? Do something. 💚</h2>
          <div className="ea-cta-btns">
            <Link to="/pickup" className="ee-btn ee-btn-green">📦 Schedule a Free Pickup</Link>
            <Link to="/mission" className="ee-btn ee-btn-blue">💚 Read our Mission</Link>
          </div>
          <p className="ea-cta-note">Got a question we didn't answer? Tap Earth Jr. in the corner — he knows his stuff. 🌎</p>
        </div>
      </section>
    </div>
  );
};

export default EcoAcademy;
